import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Paper,
  Grid,
  Select,
  Text,
  Group,
  Card,
  Stack,
  Badge,
  RingProgress,
  Loader,
  Alert,
  useMantineColorScheme,
  ActionIcon
} from '@mantine/core';
import { IconChartBar, IconCards, IconArrowLeft } from '@tabler/icons-react';
import { flashcardService } from '../services/flashcardService';
import { FlashcardEstadisticasDTO } from '../Models/Flashcard';
import { Especialidad } from '../Models/Especialidad';
import { especialidadService } from '../services/especialidadService';

const FlashcardsEstadisticasPage: React.FC = () => {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  
  // Estados
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [estadisticas, setEstadisticas] = useState<FlashcardEstadisticasDTO | null>(null);
  const [especialidades, setEspecialidades] = useState<Especialidad[]>([]);
  const [filtroEspecialidad, setFiltroEspecialidad] = useState<number | null>(null);

  // Obtener usuario
  const getUserId = (): number => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      return user.id;
    }
    return 0;
  };

  const usuarioId = getUserId();

  // Cargar datos iniciales
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [especialidadesData, estadisticasData] = await Promise.all([
          especialidadService.getEspecialidades(),
          flashcardService.obtenerEstadisticas(usuarioId, filtroEspecialidad || undefined)
        ]);
        
        setEspecialidades(especialidadesData);
        setEstadisticas(estadisticasData);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        setError('Error al cargar las estadísticas.');
      } finally {
        setLoading(false);
      }
    };

    if (usuarioId > 0) {
      loadData();
    } else {
      setError('Error: Usuario no válido');
      setLoading(false);
    }
  }, [usuarioId, filtroEspecialidad]);

  // Manejar cambio de filtro
  const handleFiltroChange = (value: string | null) => {
    setFiltroEspecialidad(value ? parseInt(value) : null);
  };

  // Navegar de vuelta
  const handleVolver = () => {
    navigate('/estudiante/flashcards');
  };

  if (loading) {
    return (
      <Container size="md" py="xl">
        <Group justify="center">
          <Loader size="lg" />
          <Text>Cargando estadísticas...</Text>
        </Group>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="md" py="xl">
        <Alert title="Error" color="red">
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="md" py="md">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" align="flex-end">
          <div style={{ textAlign: 'left' }}>
            <Title order={2} size="h3" mb="xs" style={{ textAlign: 'left' }}>
              Estadísticas de Flashcards
            </Title>
            <Text size="sm" c="dimmed" style={{ textAlign: 'left' }}>
              Revisa tu progreso y desempeño
            </Text>
          </div>
          <ActionIcon variant="default" size="lg" radius="md" onClick={handleVolver}>
            <IconArrowLeft size={18} />
          </ActionIcon>
        </Group>

        {/* Filtros */}
        <Paper shadow="none" radius="md" p="sm">
          <Select
            label="Filtrar por especialidad"
            placeholder="Todas las especialidades"
            value={filtroEspecialidad ? filtroEspecialidad.toString() : null}
            onChange={handleFiltroChange}
            data={[
              { value: '', label: 'Todas las especialidades' },
              ...especialidades.map(esp => ({
                value: esp.id.toString(),
                label: esp.nombre
              }))
            ]}
            clearable
            searchable
            size="sm"
            style={{ maxWidth: 300 }}
            styles={{
              root: {
                textAlign: 'left',
              },
              label: {
                color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: '14px',
                textAlign: 'left',
              },
              input: {
                backgroundColor: colorScheme === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : '#ffffff',
                border: `1.5px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(148, 163, 184, 0.4)'}`,
                borderRadius: '12px',
                fontSize: '15px',
                fontWeight: 500,
                color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                textAlign: 'left',
                padding: '10px 14px',
                height: '42px',
              },
              dropdown: {
                backgroundColor: colorScheme === 'dark'
                  ? 'rgba(30, 41, 59, 0.98)'
                  : '#ffffff',
                border: `1.5px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(148, 163, 184, 0.4)'}`,
                borderRadius: '12px',
                boxShadow: colorScheme === 'dark'
                  ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                  : '0 4px 16px rgba(0, 0, 0, 0.1)',
              },
              option: {
                color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                fontFamily: 'Inter, sans-serif',
                fontSize: '15px',
                padding: '10px 14px',
                borderRadius: '8px',
                '&[data-selected]': {
                  backgroundColor: colorScheme === 'dark'
                    ? 'rgba(166, 124, 82, 0.3)'
                    : 'rgba(166, 124, 82, 0.15)',
                },
                '&[data-hovered]': {
                  backgroundColor: colorScheme === 'dark'
                    ? 'rgba(166, 124, 82, 0.2)'
                    : 'rgba(166, 124, 82, 0.1)',
                },
              },
            }}
          />
        </Paper>

        {/* Estadísticas principales */}
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card shadow="none" radius="md" p="md" h="100%">
              <Group justify="space-between" mb="md">
                <Text fw={600} size="md">Progreso General</Text>
                <IconCards size={20} style={{ opacity: 0.6 }} />
              </Group>
              
              <Group justify="center" mb="md">
                <RingProgress
                  size={140}
                  thickness={14}
                  sections={[
                    { 
                      value: estadisticas?.porcentajeCompletado || 0, 
                      color: 'blue',
                      tooltip: 'Porcentaje completado'
                    }
                  ]}
                  label={
                    <Text ta="center" fw={700} size="lg">
                      {estadisticas?.porcentajeCompletado.toFixed(1)}%
                    </Text>
                  }
                />
              </Group>
              
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text size="sm">Total de flashcards:</Text>
                  <Badge variant="light" size="md">{estadisticas?.totalFlashcards || 0}</Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">Estudiadas:</Text>
                  <Badge variant="light" color="green" size="md">{estadisticas?.flashcardsEstudiadas || 0}</Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">Pendientes:</Text>
                  <Badge variant="light" color="red" size="md">{estadisticas?.flashcardsPendientes || 0}</Badge>
                </Group>
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card shadow="none" radius="md" p="md" h="100%">
              <Group justify="space-between" mb="md">
                <Text fw={600} size="md">Resumen Visual</Text>
                <IconChartBar size={20} style={{ opacity: 0.6 }} />
              </Group>
              
              {estadisticas && (
                <Stack gap="md">
                  <div>
                    <Group justify="space-between" mb="xs">
                      <Text size="sm">Estudiadas</Text>
                      <Text size="sm" fw={500}>
                        {estadisticas.flashcardsEstudiadas} / {estadisticas.totalFlashcards}
                      </Text>
                    </Group>
                    <div style={{
                      width: '100%',
                      height: '8px',
                      backgroundColor: '#e9ecef',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${estadisticas.porcentajeCompletado}%`,
                        height: '100%',
                        backgroundColor: '#228be6',
                        borderRadius: '4px',
                        transition: 'width 0.5s ease'
                      }} />
                    </div>
                  </div>

                  <div>
                    <Group justify="space-between" mb="xs">
                      <Text size="sm">Pendientes</Text>
                      <Text size="sm" fw={500}>
                        {estadisticas.flashcardsPendientes}
                      </Text>
                    </Group>
                    <div style={{
                      width: '100%',
                      height: '8px',
                      backgroundColor: '#e9ecef',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${100 - estadisticas.porcentajeCompletado}%`,
                        height: '100%',
                        backgroundColor: '#fd7e14',
                        borderRadius: '4px',
                        transition: 'width 0.5s ease'
                      }} />
                    </div>
                  </div>
                </Stack>
              )}
            </Card>
          </Grid.Col>
        </Grid>
      </Stack>
    </Container>
  );
};

export default FlashcardsEstadisticasPage;